import * as THREE from 'three';

// Feedback when something takes damage: a bright spark on the unit that was
// hit and, for the local player, a red pulse around the edge of the screen.
// Theme-independent, so every theme gets the same readable cue.
const SPARK_LIFETIME = 0.35;
const SPARK_GEOMETRY = new THREE.IcosahedronGeometry(1, 1);
const RING_GEOMETRY = new THREE.RingGeometry(0.7, 1, 24).rotateX(-Math.PI / 2);

export class HitEffects {
  constructor(scene) {
    this.scene = scene;
    this.sparks = [];
    this.vignette = null;
    this.vignetteLevel = 0;
  }

  // A flash and expanding ring at the point of impact.
  spark(position, size = 1) {
    const flash = new THREE.Mesh(SPARK_GEOMETRY, new THREE.MeshBasicMaterial({
      color: 0xffe0c0,
      transparent: true,
      opacity: 0.9,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    }));
    const ring = new THREE.Mesh(RING_GEOMETRY, new THREE.MeshBasicMaterial({
      color: 0xff3b2f,
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide
    }));
    const group = new THREE.Group();
    group.add(flash, ring);
    group.position.set(position.x, position.y + 1.2 * size, position.z);
    group.userData = { age: 0, size, flash, ring };
    this.scene.add(group);
    this.sparks.push(group);
  }

  // Red pulse around the screen edge; strength 0..1.
  hurt(strength = 1) {
    if (!this.vignette) {
      this.vignette = document.createElement('div');
      this.vignette.id = 'damage-vignette';
      document.body.appendChild(this.vignette);
    }
    this.vignetteLevel = Math.min(1, this.vignetteLevel + 0.5 + strength * 0.5);
  }

  update(deltaTime) {
    this.sparks = this.sparks.filter(group => {
      const data = group.userData;
      data.age += deltaTime;
      const t = data.age / SPARK_LIFETIME;
      if (t >= 1) {
        this.scene.remove(group);
        data.flash.material.dispose();
        data.ring.material.dispose();
        return false;
      }
      data.flash.scale.setScalar(data.size * (0.6 + t * 0.6));
      data.flash.material.opacity = 0.9 * (1 - t);
      data.ring.scale.setScalar(data.size * (0.8 + t * 2.4));
      data.ring.material.opacity = 0.8 * (1 - t);
      return true;
    });

    if (this.vignette) {
      this.vignetteLevel = Math.max(0, this.vignetteLevel - deltaTime * 1.8);
      this.vignette.style.opacity = this.vignetteLevel.toFixed(3);
    }
  }

  clear() {
    for (const group of this.sparks) {
      this.scene.remove(group);
      group.userData.flash.material.dispose();
      group.userData.ring.material.dispose();
    }
    this.sparks = [];
    this.vignetteLevel = 0;
    if (this.vignette) this.vignette.style.opacity = '0';
  }
}
